import {
  Component,
  Input,
  Type,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ConfirmDialogComponent, ConfirmDialogData } from "@cotecna/dialogs";
import { ControlType, Field } from "@cotecna/dynamic-view-engine";
import { AdditionalInfoDialogComponent } from "src/app/inspection/dialogs/additional-info-dialog/additional-info-dialog.component";
import { GeneralInfo } from "src/app/inspection/model/general-info";
import { AdditionalInfoInputComponent } from "src/app/inspection/new-inspection/additional-info-input/additional-info-input.component";
import { GeneralInfoService } from "src/app/inspection/services/general-info.service";
import { ADDITIONAL_FIELDS_FORM } from "src/app/inspection/static-forms/general-info-form";
import { AdditionalFieldControlType } from "src/app/settings/models/preset";
import { newGuid } from "src/app/shared/utils/guid-utils";

@Component({
  selector: "im-additional-fields-editable-mode",
  templateUrl: "./additional-fields-editable-mode.html",
  styleUrls: ["./additional-fields-editable-mode.scss"],
  standalone: false,
})
export class AdditionalFieldsEditableModeComponent {
  @ViewChild("container", { read: ViewContainerRef })
  container: ViewContainerRef;

  @Input()
  public generalInfo: GeneralInfo;

  public additionalFields: Field[] = [];

  constructor(
    public dialog: MatDialog,
    public generalInfoService: GeneralInfoService,
  ) {}

  ngOnInit() {
    this.additionalFields = [];
    setTimeout(() => {
      this.generalInfo.additionalFields.forEach((other, index) => {
        this.manageGeneralInfoOtherField(
          other.name,
          other.value,
          index,
          other.controlType,
          other.selectionOptions,
          other.mandatory,
          other.id,
        );
      });
    });
  }

  ngOnDestroy() {
    this.generalInfoService.removeAllOthersComponents();
  }

  public async addField(): Promise<void> {
    const dialogRef = this.openAdditionalInfoDialog(
      "Add Additional Field",
      "ADD",
      false,
    );
    const result = await dialogRef.afterClosed().toPromise();
    if (result?.data) {
      this.manageGeneralInfoOtherField(
        result.data.fieldname,
        result.data.value,
        0,
        AdditionalFieldControlType.Input,
        [],
        false,
      );
    }
  }

  private manageGeneralInfoOtherField(
    label: string,
    value: string,
    index: number,
    controlType: AdditionalFieldControlType,
    selectableOptions: string[],
    mandatory: boolean,
    id?: string,
  ) {
    const fieldToAdd: Field = id
      ? this.getNewInputField(
          label,
          value,
          index,
          controlType,
          selectableOptions,
          mandatory,
          id,
        )
      : this.getNewInputField(
          label,
          value,
          index,
          controlType,
          selectableOptions,
          mandatory,
        );
    if (fieldToAdd) {
      this.additionalFields.push(fieldToAdd);
      this.addComponent(
        AdditionalInfoInputComponent,
        fieldToAdd,
        controlType,
        mandatory,
      );
    }
  }

  private openAdditionalInfoDialog(
    title: string,
    actionButtonText: string,
    mandatory: boolean,
    field?: Field,
  ): MatDialogRef<any> {
    return this.dialog.open(AdditionalInfoDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: {
        title: title,
        actionButtonText: actionButtonText,
        others: this.generalInfo.additionalFields,
        availablePrefilledOthers: this.generalInfo.availablePrefilledOthers,
        fieldName: field ? field.label : "",
        value: field ? field.value : "",
        mandatory: mandatory,
        type: field ? field.type : ControlType.Input,
      },
    });
  }

  private removeOthersFieldCallback(fieldId: string) {
    const positionToRemove =
      this.generalInfoService.getOthersComponentIndex(fieldId);
    this.removeOthersFieldsFromDOM(positionToRemove);
  }

  private getNewInputField(
    label: string,
    value: string,
    order: number,
    controlType: AdditionalFieldControlType,
    selectableOptions: string[],
    mandatory: boolean,
    id?: string,
  ): Field {
    return {
      id: id ? id : newGuid(),
      type:
        controlType == AdditionalFieldControlType.Selection
          ? ControlType.Dropdown
          : ControlType.Input,
      label,
      order,
      propertyName: label?.toLocaleLowerCase(),
      value,
      required: mandatory,
      visible: true,
      valid: true,
      readableValue: selectableOptions?.length ? selectableOptions : value,
    } as Field;
  }

  private addComponent(
    additionalInfoInput: Type<any>,
    field: Field,
    controlType: AdditionalFieldControlType,
    mandatory: boolean,
  ) {
    const component = this.container.createComponent(additionalInfoInput);
    const instance: any = <AdditionalInfoInputComponent>component.instance;
    component.instance.mandatory = mandatory;
    instance.field = field;
    instance.controlType = controlType;
    instance.mandatory = mandatory;
    instance.remove.subscribe((field: Field) => this.removeOthersField(field));
    instance.edit.subscribe((field: Field) =>
      this.editOthersField(field, mandatory),
    );
    instance.update.subscribe((field: Field) =>
      this.editOthersFieldCallback(field),
    );

    this.generalInfoService.addOthersFieldComponent(component);
  }

  private async removeOthersField(field: Field) {
    const fieldLabel = this.generalInfoService
      .getOthersComponents()
      .find((component) => component.instance.field.id == field.id).instance
      .field.label;

    const removeFieldDialog = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "clear",
        okButton: { text: "delete", color: "warn" },
        title: " Delete Additional Field",
        message: `Are you sure you want to delete the ${fieldLabel} field?`,
      },
    });

    const result = await removeFieldDialog.afterClosed().toPromise();
    if (result) this.removeOthersFieldCallback(field.id);
  }

  private removeOthersFieldsFromDOM(otherFieldPosition: number) {
    if (otherFieldPosition > -1) {
      this.container?.remove(otherFieldPosition);
      this.generalInfoService.spliceOthersComponent(otherFieldPosition);
    }
  }

  private async editOthersField(value: any, mandatory: boolean): Promise<void> {
    const sections = JSON.parse(JSON.stringify(ADDITIONAL_FIELDS_FORM));
    sections[0].fields.filter((field) => field.id == "fieldname")[0].value =
      value.field.label;
    sections[0].fields.filter((field) => field.id == "value")[0].value =
      value.field.value;

    const editFieldDialog = this.openAdditionalInfoDialog(
      "Edit Additional Field",
      "EDIT",
      mandatory,
      value.field,
    );
    const result: any = await editFieldDialog.afterClosed().toPromise();
    if (result.data) {
      value.field.label = result.data.fieldname;
      value.field.value = result.data.value;
      this.editOthersFieldCallback(value);
    }
  }

  private editOthersFieldCallback(value: any) {
    const positionToEdit = this.generalInfoService.getOthersComponentIndex(
      value.field.id,
    );
    if (positionToEdit > -1) {
      if (value.controlType == AdditionalFieldControlType.Selection) {
        const i = value.field.readableValue.findIndex(
          (rv) => rv == value.field.value,
        );
        if (i == -1) {
          value.field.readableValue.push(value.field.value);
          this.generalInfoService.setOthersComponentReadableValue(
            positionToEdit,
            value.field.readableValue,
          );
        }
      }
      this.generalInfoService.setOthersComponentLabel(
        positionToEdit,
        value.field.label,
      );
      this.generalInfoService.setOthersComponentValue(
        positionToEdit,
        value.field.value,
      );
      this.generalInfoService.setOthersComponentControlType(
        positionToEdit,
        value.controlType,
      );
    }
  }
}
